import * as THREE from 'three';
import { COLLECTIBLES, MISSIONS, type MissionDefinition } from './missionData';
import { telemetry } from '../ui/telemetry';

export interface MissionCallbacks {
  onCollect: (id: string) => void;
  onMissionComplete: (id: string, coins: number, stars: number) => void;
}

export class MissionSystem {
  readonly group = new THREE.Group();
  private readonly collected: Set<string>;
  private readonly callbacks: MissionCallbacks;
  private readonly startMarkers: THREE.Group[] = [];
  private readonly targetMarker: THREE.Group;
  private readonly collectibleMesh: THREE.InstancedMesh;
  private readonly matrix = new THREE.Matrix4();
  private readonly position = new THREE.Vector3();
  private readonly quaternion = new THREE.Quaternion();
  private readonly scale = new THREE.Vector3(1, 1, 1);
  private active: MissionDefinition | null = null;
  private checkpoint = 0;
  private cooldownUntil = 0;
  private enabled = true;

  constructor(scene: THREE.Scene, collectedIds: readonly string[], callbacks: MissionCallbacks) {
    this.collected = new Set(collectedIds);
    this.callbacks = callbacks;

    for (const mission of MISSIONS) {
      const marker = makeMarker(0x4bd36a, 3.6);
      const start = mission.route[0];
      if (start) marker.position.set(start.x, 0.12, start.z);
      this.group.add(marker);
      this.startMarkers.push(marker);
    }

    this.targetMarker = makeMarker(0xffc21a, 4.4);
    this.targetMarker.visible = false;
    this.group.add(this.targetMarker);

    const coinGeo = new THREE.CylinderGeometry(0.72, 0.72, 0.22, 12);
    coinGeo.rotateZ(Math.PI / 2);
    const coinMat = new THREE.MeshStandardMaterial({
      color: 0xffc21a,
      emissive: 0x8a5200,
      emissiveIntensity: 0.8,
      metalness: 0.55,
      roughness: 0.25,
    });
    this.collectibleMesh = new THREE.InstancedMesh(coinGeo, coinMat, COLLECTIBLES.length);
    this.collectibleMesh.castShadow = true;
    this.group.add(this.collectibleMesh);
    scene.add(this.group);
    this.writeCollectibleMatrices(0);
  }

  update(player: THREE.Vector3, now: number): void {
    if (!this.enabled) return;
    this.group.traverse((object) => {
      if (object.userData['spin'] === true) object.rotation.z = now * 0.0012;
    });
    this.writeCollectibleMatrices(now);

    for (const item of COLLECTIBLES) {
      if (this.collected.has(item.id)) continue;
      if (distanceSq(player.x, player.z, item.x, item.z) < 10) {
        this.collected.add(item.id);
        this.callbacks.onCollect(item.id);
      }
    }

    if (!this.active) {
      telemetry.missionTitle = '';
      telemetry.missionProgress = 0;
      telemetry.missionHint = 'FAHRE ZUM GRÜNEN STARTFELD';
      if (now < this.cooldownUntil) return;
      for (let i = 0; i < MISSIONS.length; i++) {
        const mission = MISSIONS[i];
        const start = mission?.route[0];
        if (!mission || !start) continue;
        if (distanceSq(player.x, player.z, start.x, start.z) < 20) {
          this.start(mission);
          break;
        }
      }
      return;
    }

    const target = this.active.route[this.checkpoint];
    if (!target) return;
    telemetry.missionTitle = `${this.active.icon} ${this.active.title}`;
    telemetry.missionHint = this.active.hint;
    telemetry.missionProgress = this.checkpoint / (this.active.route.length - 1);
    this.targetMarker.position.set(target.x, 0.12, target.z);

    if (distanceSq(player.x, player.z, target.x, target.z) < 22) {
      this.checkpoint += 1;
      if (this.checkpoint >= this.active.route.length) this.finish(now);
    }
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.group.visible = enabled;
    if (!enabled) {
      telemetry.missionTitle = '';
      telemetry.missionHint = '';
      telemetry.missionProgress = 0;
    }
  }

  reset(): void {
    this.active = null;
    this.checkpoint = 0;
    this.cooldownUntil = 0;
    this.targetMarker.visible = false;
    this.startMarkers.forEach((marker) => { marker.visible = this.enabled; });
    telemetry.missionTitle = '';
    telemetry.missionHint = this.enabled ? 'FAHRE ZUM GRÜNEN STARTFELD' : '';
    telemetry.missionProgress = 0;
  }

  private start(mission: MissionDefinition): void {
    this.active = mission;
    this.checkpoint = 1;
    this.startMarkers.forEach((marker) => { marker.visible = false; });
    this.targetMarker.visible = true;
  }

  private finish(now: number): void {
    if (!this.active) return;
    this.callbacks.onMissionComplete(this.active.id, this.active.rewardCoins, this.active.rewardStars);
    telemetry.missionTitle = 'GESCHAFFT! ⭐';
    telemetry.missionHint = `+${this.active.rewardCoins} MÜNZEN`;
    telemetry.missionProgress = 1;
    this.active = null;
    this.cooldownUntil = now + 4500;
    this.targetMarker.visible = false;
    window.setTimeout(() => {
      this.startMarkers.forEach((marker) => { marker.visible = true; });
    }, 4500);
  }

  private writeCollectibleMatrices(now: number): void {
    for (let i = 0; i < COLLECTIBLES.length; i++) {
      const item = COLLECTIBLES[i];
      if (!item) continue;
      this.position.set(item.x, 1.45 + Math.sin(now * 0.003 + i) * 0.2, item.z);
      this.quaternion.setFromEuler(new THREE.Euler(0, now * 0.002 + i, 0));
      const s = this.collected.has(item.id) ? 0 : 1;
      this.scale.setScalar(s);
      this.matrix.compose(this.position, this.quaternion, this.scale);
      this.collectibleMesh.setMatrixAt(i, this.matrix);
    }
    this.collectibleMesh.instanceMatrix.needsUpdate = true;
  }
}

function makeMarker(color: number, radius: number): THREE.Group {
  const group = new THREE.Group();
  const ring = new THREE.Mesh(
    new THREE.TorusGeometry(radius, 0.28, 8, 32),
    new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 1.25, roughness: 0.4 }),
  );
  ring.rotation.x = Math.PI / 2;
  ring.userData['spin'] = true;
  group.add(ring);
  const beam = new THREE.Mesh(
    new THREE.CylinderGeometry(radius * 0.72, radius * 0.95, 0.08, 24),
    new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.45 }),
  );
  group.add(beam);
  return group;
}

function distanceSq(ax: number, az: number, bx: number, bz: number): number {
  const dx = ax - bx;
  const dz = az - bz;
  return dx * dx + dz * dz;
}
